// src/pages/Historico/components/ExperimentHeader.jsx
import React from 'react';
import {
    Paper, Stack, Avatar, Box, Typography, Chip, Divider,
    Grid, Button, IconButton, useTheme, useMediaQuery,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import dayjs from 'dayjs';

const Detail = ({ icon, primary, secondary }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon}
        <Box>
            <Typography variant="body2" color="text.secondary">{primary}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }} noWrap title={secondary}>
                {secondary || '—'}
            </Typography>
        </Box>
    </Box>
);

export default function ExperimentHeader({ experiment, experimentId, onBack }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { serialNumber, protocolName, analysisName, startDate, endDate, status, protocolColor } = experiment;
    const isActive = (status || '').toLowerCase() === 'active';
    const startMs = Number.isFinite(Number(startDate)) ? Number(startDate) * 1000 : null;
    const endMs = Number.isFinite(Number(endDate)) ? Number(endDate) * 1000 : null;

    return (
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Avatar sx={{
                        bgcolor: protocolColor || 'primary.main', color: '#fff',
                        width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 },
                        fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    }}>
                        {protocolName?.[0]?.toUpperCase() || '—'}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" sx={{ fontWeight: 'bold' }} noWrap>
                            SN: {serialNumber}
                        </Typography>
                        <Chip icon={<Lucide.Info size={18} />} label={`ID: ${experimentId}`} size="small"
                            sx={{ mt: 0.5, maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                        />
                    </Box>
                </Stack>
                <Box sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                    <Button variant="outlined" startIcon={<Lucide.ArrowLeft />} onClick={onBack}>Voltar</Button>
                </Box>
                <Box sx={{ ml: 1, display: { xs: 'block', sm: 'none' } }}>
                    <IconButton onClick={onBack}><Lucide.ArrowLeft /></IconButton>
                </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={6}>
                    <Detail icon={<Lucide.FlaskConical color={theme.palette.primary.main} />} primary="Protocolo" secondary={protocolName} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Detail icon={<Lucide.SlidersHorizontal color={theme.palette.primary.main} />} primary="Análise" secondary={analysisName} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Detail icon={<Lucide.Calendar color={theme.palette.action.active} />} primary="Início"
                        secondary={startMs ? dayjs(startMs).format('DD/MM/YYYY HH:mm') : '—'} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Detail
                        icon={<Lucide.Clock color={isActive ? theme.palette.warning.main : theme.palette.success.main} />}
                        primary={isActive ? 'Estimativa de Fim' : 'Fim'}
                        secondary={
                            isActive && startMs
                                ? dayjs(startMs).add(24, 'hour').format('DD/MM/YYYY HH:mm')
                                : endMs ? dayjs(endMs).format('DD/MM/YYYY HH:mm') : '—'
                        }
                    />
                </Grid>
            </Grid>
        </Paper>
    );
}
